import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import { TabGroup } from '@/kit/Tabs';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { periodRangeText } from '@/core/format/periodLabel';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { selectedClient, type SelectedClient } from '@/core/selectedClient';
import { fixedAssetService } from '@/core/services/fixedAsset';
import { practiceService } from '@/core/services/practice';
import type {
  FixedAssetClassTotal,
  FixedAssetMovementRow,
  FixedAssetStaleRow,
  FixedAssetSyncView,
} from '@/core/models/phase7.models';
import { decimalPipe } from './format';
import { useMounted } from './useMounted';
import './LeafTables.scss';
import './FixedAssetSync.scss';

interface PeriodOption {
  id: string;
  name: string;
}

function staleLabel(objectType: string): string {
  switch (objectType) {
    case 'FixedAssetSchedule':
      return 'Fixed asset schedule';
    case 'FixedAssetNote':
      return 'Fixed asset note';
    case 'AccountsDocument':
      return 'Accounts document';
    default:
      return objectType;
  }
}

/** Angular's `date: 'dd/MM/yyyy HH:mm'` (local time). */
function staleSince(value: string): string {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : '';
}

const rightHeader = 'text-align-right';

const classColumns: DataTableColumn<FixedAssetClassTotal>[] = [
  { id: 'classCode', header: 'Class', cell: (row) => row.classCode },
  { id: 'className', header: 'Description', cell: (row) => row.className },
  {
    id: 'assetCount',
    header: 'Assets',
    headerClassName: 'text-align-center',
    cellClassName: 'text-align-center',
    cell: (row) => row.assetCount,
  },
  {
    id: 'costBroughtForward',
    header: 'Cost b/f',
    headerClassName: rightHeader,
    cellClassName: rightHeader,
    cell: (row) => decimalPipe(row.costBroughtForward),
  },
  {
    id: 'additions',
    header: 'Additions',
    headerClassName: rightHeader,
    cellClassName: rightHeader,
    cell: (row) => decimalPipe(row.additions),
  },
  {
    id: 'disposals',
    header: 'Disposals',
    headerClassName: rightHeader,
    cellClassName: rightHeader,
    cell: (row) => decimalPipe(row.disposals),
  },
  {
    id: 'costCarriedForward',
    header: 'Cost c/f',
    headerClassName: rightHeader,
    cellClassName: rightHeader,
    cell: (row) => decimalPipe(row.costCarriedForward),
  },
  {
    id: 'depnCarriedForward',
    header: 'Depreciation c/f',
    headerClassName: rightHeader,
    cellClassName: rightHeader,
    cell: (row) => decimalPipe(row.depnCarriedForward),
  },
  {
    id: 'netBookValue',
    header: 'Net book value',
    headerClassName: rightHeader,
    cellClassName: `${rightHeader} fw-bold`,
    cell: (row) => decimalPipe(row.netBookValue),
  },
];

const movementColumns: DataTableColumn<FixedAssetMovementRow>[] = [
  { id: 'assetName', header: 'Asset', cell: (row) => row.assetName },
  { id: 'sourceAssetId', header: 'Register id', cell: (row) => row.sourceAssetId },
  { id: 'classCode', header: 'Class', cell: (row) => row.classCode },
  {
    id: 'costCarriedForward',
    header: 'Cost c/f',
    headerClassName: rightHeader,
    cellClassName: rightHeader,
    cell: (row) => decimalPipe(row.costCarriedForward),
  },
  {
    id: 'depnCharge',
    header: 'Charge',
    headerClassName: rightHeader,
    cellClassName: rightHeader,
    cell: (row) => decimalPipe(row.depnCharge),
  },
  {
    id: 'depnCarriedForward',
    header: 'Depreciation c/f',
    headerClassName: rightHeader,
    cellClassName: rightHeader,
    cell: (row) => decimalPipe(row.depnCarriedForward),
  },
  {
    id: 'netBookValue',
    header: 'Net book value',
    headerClassName: rightHeader,
    cellClassName: `${rightHeader} fw-bold`,
    cell: (row) => decimalPipe(row.netBookValue),
  },
];

export function FixedAssetSync() {
  const navigate = useNavigate();
  const mounted = useMounted();
  const [client] = useState<SelectedClient | null>(() => selectedClient());

  const [selectedTab, setSelectedTab] = useState(0);
  const [periods, setPeriods] = useState<PeriodOption[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [view, setView] = useState<FixedAssetSyncView | null>(null);

  const isConfigured = view?.isConfigured ?? true;
  const staleCount = view?.staleObjects.length ?? 0;

  const load = useCallback(
    (periodId: string | null) => {
      if (!periodId) return;
      fixedAssetService
        .getSync(periodId)
        .then((result) => {
          if (mounted.current) setView(result);
        })
        .catch((error) => {
          if (mounted.current) notifyError(error);
        });
    },
    [mounted],
  );

  useEffect(() => {
    if (!client) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
      return;
    }
    practiceService
      .getClientPeriods(client.id)
      .then((response) => {
        if (!mounted.current) return;
        const options = (response.periods ?? []).map((p) => ({ id: p.id, name: periodRangeText(p.periodFrom, p.periodTo, ' to ') }));
        setPeriods(options);
        if (options.length > 0) {
          setSelectedPeriodId(options[0].id);
          load(options[0].id);
        }
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
    // Runs once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPeriodChange = (periodId: string | null) => {
    setSelectedPeriodId(periodId);
    setView(null);
    load(periodId);
  };

  /** Reads the register and rebuilds the fixed-asset schedule from it. */
  const sync = () => {
    if (!selectedPeriodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    fixedAssetService
      .syncPeriod(selectedPeriodId)
      .then((response) => {
        if (!mounted.current) return;
        setView(response.view);
        notifySuccess(response.message);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  const clearStale = (staleObjectId: string) => {
    if (!selectedPeriodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    fixedAssetService
      .clearStale(selectedPeriodId, staleObjectId)
      .then((result) => {
        if (!mounted.current) return;
        setView(result);
        notifySuccess('Marked as revalidated.');
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  const staleColumns: DataTableColumn<FixedAssetStaleRow>[] = [
    { id: 'objectType', header: 'Item', cell: (row) => staleLabel(row.objectType) },
    { id: 'reason', header: 'Why', cell: (row) => row.reason },
    { id: 'syncVersion', header: 'Sync', cell: (row) => row.syncVersion },
    { id: 'staleSinceUtc', header: 'Since', cell: (row) => staleSince(row.staleSinceUtc) },
    {
      id: 'action',
      header: 'Action',
      cell: (row) => (
        <Button onClick={() => clearStale(row.id)}>
          <Icon name="task_alt" outlined />
          Mark revalidated
        </Button>
      ),
    },
  ];

  return (
    <SideMenuPage menu="settings" contentClassName="fixed-asset-sync">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Select<PeriodOption>
              className="w-300"
              openOnFocus
              panelClass="myPanelClass"
              items={periods}
              bindLabel="name"
              bindValue="id"
              placeholder="Select an accounting period"
              clearable={false}
              value={selectedPeriodId}
              onChange={(value) => onPeriodChange(value as string | null)}
            />

            <Button
              className="action-button primary-button ml-1"
              disabled={!selectedPeriodId || !isConfigured}
              onClick={sync}
            >
              <Icon name="sync" />
              <span>Sync from Fixed Asset Register</span>
            </Button>
          </div>

          {view && view.syncVersion !== null && (
            <div className="content-header-right mb-1">
              <span className="fw-bold">
                Sync version {view.syncVersion} &middot; {view.assetCount} asset(s)
                {view.scheduleStatus && <span> &middot; schedule {view.scheduleStatus}</span>}
              </span>
            </div>
          )}
        </div>

        {/* No register endpoint configured: say so rather than showing invented figures. */}
        {view && !view.isConfigured && (
          <div className="form-field">
            <p className="text-danger">
              <Icon name="info_outline" className="align-middle" /> {view.message}
            </p>
          </div>
        )}

        {view && view.isConfigured && view.message && (
          <div className="form-field">
            <p>{view.message}</p>
          </div>
        )}

        {/* Anything a later sync invalidated. */}
        {view && staleCount > 0 && (
          <div className="form-field">
            <p className="text-danger fw-bold">
              <Icon name="warning_amber" className="align-middle" /> The register changed after these were prepared.
              They need revalidation.
            </p>
            <div className="mat-table-wrapper mat-table-wrapper-stale">
              <DataTable
                stickyHeader
                className="tr-leaf-sticky-header"
                columns={staleColumns}
                rows={view.staleObjects}
                rowKey={(row) => row.id}
              />
            </div>
          </div>
        )}

        <TabGroup
          alignTabs="start"
          selectedIndex={selectedTab}
          onSelectedIndexChange={setSelectedTab}
          tabs={[
            {
              label: 'By asset class',
              children: (
                <>
                  <p className="mb-1 pt-1">
                    The fixed-asset schedule and note are built from these class totals. Accounts Production stores the
                    register's own asset and class identifiers, so the figures stay traceable to their source.
                  </p>
                  <div className="mat-table-wrapper">
                    <DataTable
                      stickyHeader
                      className="tr-leaf-sticky-header"
                      columns={classColumns}
                      rows={view ? view.classTotals : []}
                      rowKey={(row) => row.classCode}
                    />
                    {(!view || view.classTotals.length === 0) && (
                      <p className="mt-1">No fixed asset data has been synced for this period.</p>
                    )}
                  </div>
                </>
              ),
            },
            {
              label: 'By asset',
              children: (
                <div className="mat-table-wrapper pt-1">
                  <DataTable
                    stickyHeader
                    className="tr-leaf-sticky-header"
                    columns={movementColumns}
                    rows={view ? view.movements : []}
                    rowKey={(row) => row.id}
                  />
                  {(!view || view.movements.length === 0) && (
                    <p className="mt-1">No fixed asset data has been synced for this period.</p>
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>
    </SideMenuPage>
  );
}
